import React from 'react';

import { Separator, Spinner } from '@change/ds-core-ui';

import { useI18n, withI18nPlaceholders } from '@change-corgi/core-react/i18n';

import I18N_PLACEHOLDERS from '#config/i18n/placeholders/petitionDetails/petition-details.yml';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import { usePetitionShareCounts, useScrollToSection } from '#src/app/pages/petitionDetails/shared/hooks';

import { useMomentumBarShareAction } from '../../hooks/useMomentumBarShareAction';
import { useMomentumBarVideoPreview } from '../../hooks/useMomentumBarVideoPreview';
import { useSpotlightCardContent } from '../../hooks/useSpotlightCardContent';
import { useMomentumBarTracking } from '../../hooks/useTracking';
import { MomentumBarSubmodules } from '../MomentumBarSubmodules';
import { SignersCarousel } from '../SignersCarousel';
import { SpotlightCard } from '../SpotlightCard';

type Props = Readonly<{
	shouldFetchShareCounts?: boolean;
}>;

const MomentumBarComponent: React.FC<Props> = ({ shouldFetchShareCounts }) => {
	const {
		data: {
			petition: { supporterCommentsConnection, recentPublicSignersConnectionV2 },
			mediaSourcesPage: { numberOfMediaSources },
		},
	} = usePetitionDetailsPageData();
	const { translate } = useI18n();
	const tracking = useMomentumBarTracking();
	const spotlightItems = useSpotlightCardContent();
	const {
		totalShareCount,
		totalShareCountLocalized,
		isLoading: isShareCountsLoading,
	} = usePetitionShareCounts({
		enabled: shouldFetchShareCounts !== false,
	});
	const { isNativeShareAvailable, handleSharesClick } = useMomentumBarShareAction(tracking);
	const {
		actions: { scrollToSection },
	} = useScrollToSection();
	const { videoPreview } = useMomentumBarVideoPreview(tracking.trackVideoPreviewView, scrollToSection);

	const commentsCount = supporterCommentsConnection.totalCount;
	const sharesCount = totalShareCount ?? 0;
	// The displayed count is every media hit on the petition, but the slot stays gated on media sources: they are what
	// supply the outlet logos, so hits with no identified source would render a counter with no logos. Sources are
	// derived from hits, so whenever the slot shows, the count is at least 1.
	const hasMediaMentions = numberOfMediaSources > 0;
	const recentSigners = recentPublicSignersConnectionV2.edges;

	// Show a loader without the bordered container until share counts have loaded.
	if (isShareCountsLoading) {
		return (
			<div className="mb-8 flex min-h-[120px] w-full items-center justify-center" data-qa="momentum-bar-loading">
				<Spinner className="size-6" />
			</div>
		);
	}

	// Require at least comments, plus either shares or media mentions. Hide bar if no shares and no media mentions.
	if (commentsCount < 1 || (sharesCount === 0 && !hasMediaMentions)) {
		return null;
	}

	return (
		<div
			className="border-stroke-weaker mb-8 flex flex-col items-start self-stretch rounded-lg border p-3 md:p-0 md:pt-4 md:pr-3 md:pb-0 md:pl-4"
			data-qa="momentum-bar-container"
		>
			<div className="flex w-full flex-col items-start gap-0.5 p-0 lg:flex-row">
				<SpotlightCard
					items={spotlightItems}
					animatedCardsCarousel
					testId="momentum-bar-spotlight"
					qaId="momentum-bar-spotlight"
				/>
				<MomentumBarSubmodules
					hasShares={sharesCount > 0}
					hasMediaMentions={hasMediaMentions}
					totalShareCountLocalized={totalShareCountLocalized}
					isNativeShareAvailable={isNativeShareAvailable}
					onSharesClick={handleSharesClick}
					videoPreview={videoPreview}
				/>
			</div>
			<Separator className="my-2.5 lg:hidden" />
			<div className="w-full max-w-full min-w-0 overflow-hidden motion-reduce:hidden">
				<SignersCarousel
					signers={recentSigners}
					testId="momentum-bar-signers-row"
					qaId="momentum-bar-signers-row"
					anonymousLabel={translate('corgi.petition_details.momentum_bar.recent_signers_anonymous')}
				/>
			</div>
		</div>
	);
};

export const MomentumBar = withI18nPlaceholders(I18N_PLACEHOLDERS, MomentumBarComponent);
